// src/modals/CouponCreateDialog.jsx
import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import trLocale from "date-fns/locale/tr";
import Services from "src/services/Services";
import { toast } from "react-toastify";
import api from "src/services/BaseService";

const defaultForm = {
  name: "",
  locationId: "",
  discountForProduct: "all",
  startDate: null,
  endDate: null,
  discountType: "percentage", // "percentage" | "amount"
  discountValue: "",
  maxUsage: "", // Tekil: maksimum kullanım / Toplu: oluşturulacak kupon adedi
  couponCode: "",
  isBulk: false,
};

export default function CouponCreateDialog({
  open,
  onClose,
  onSubmit,
  locations = [],
}) {
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bulkSuccessModalOpen, setBulkSuccessModalOpen] = useState(false);
  const [bulkCodes, setBulkCodes] = useState([]);
  const [bulkMeta, setBulkMeta] = useState(null);

  const discountTypeLabel = useMemo(
    () => (form.discountType === "percentage" ? "Oran (%)" : "Miktar"),
    [form.discountType]
  );

  const maxUsageLabel = useMemo(
    () => (form.isBulk ? "Kupon Adedi" : "Maksimum Kullanım"),
    [form.isBulk]
  );

  const setField = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => ({ ...p, [key]: "" }));
    setApiError("");
  };

  const isCouponCreateSuccess = (data) =>
    data === true ||
    data?.result === true ||
    data?.status === true ||
    data?.success === true;

  const DUPLICATE_COUPON_MESSAGE =
    "Aynı kupon kodu daha önce kullanıldı, lütfen yeni kupon kodu ile deneyin.";

  const toTurkishMessage = (message) => {
    if (!message || typeof message !== "string") return "";
    if (message.includes("Coupon  already exists")) {
      return DUPLICATE_COUPON_MESSAGE;
    }
    if (message.includes("Aynı kupon kodu daha önce kullanıldı")) {
      return DUPLICATE_COUPON_MESSAGE;
    }
    return message;
  };

  const isDuplicateCouponError = (data) => {
    const message = data?.message || data?.error || "";
    return (
      data?.status === false &&
      typeof message === "string" &&
      (message.includes("Aynı kupon kodu daha önce kullanıldı") ||
        message.includes("Coupon  already exists"))
    );
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Zorunlu alan";
    if (!form.locationId) e.locationId = "Zorunlu alan";
    if (!form.startDate) e.startDate = "Zorunlu alan";
    if (!form.endDate) e.endDate = "Zorunlu alan";
    if (form.startDate && form.endDate && form.endDate < form.startDate)
      e.endDate = "Bitiş tarihi başlangıçtan önce olamaz";

    if (!form.discountType) e.discountType = "Zorunlu alan";
    if (form.discountValue === "" || isNaN(Number(form.discountValue)))
      e.discountValue = "Geçerli bir sayı girin";
    else if (form.discountType === "percentage") {
      const v = Number(form.discountValue);
      if (v <= 0 || v > 100) e.discountValue = "0-100 arasında olmalı";
    } else if (Number(form.discountValue) <= 0) {
      e.discountValue = "0'dan büyük olmalı";
    }

    // Tekil/Toplu durumuna göre alan validasyonları
    if (form.isBulk) {
      // Toplu: kupon adedi zorunlu ve pozitif tam sayı olmalı
      if (
        form.maxUsage === "" ||
        !Number.isInteger(Number(form.maxUsage)) ||
        Number(form.maxUsage) <= 0
      ) {
        e.maxUsage = "Pozitif bir tam sayı girin";
      }
    } else {
      // Tekil: kupon kodu zorunlu, maxUsage ise opsiyonel ama negatif olamaz
      if (!form.couponCode.trim()) e.couponCode = "Zorunlu alan";
      if (
        form.maxUsage !== "" &&
        (!Number.isInteger(Number(form.maxUsage)) || Number(form.maxUsage) < 0)
      ) {
        e.maxUsage = "Geçerli bir tam sayı girin";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // YYYY-MM-DD (yerel zaman) — UTC kaymasını engeller
  const toYMDLocal = (d) => {
    if (!d) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleCreate = async () => {
    setApiError("");
    if (!validate()) return;

    const title = form.name.trim();
    const mallID = form.locationId;
    const discountForProduct = form.discountForProduct;
    const startDate = toYMDLocal(form.startDate);
    const expireDate = toYMDLocal(form.endDate);
    const discountType = form.discountType; // "percentage" | "amount"
    const discountValue = Number(form.discountValue);

    const parsedUser = JSON.parse(localStorage.getItem("user") || "{}");

    try {
      setIsSubmitting(true);

      if (form.isBulk) {
        // TOPLU KUPON OLUŞTURMA
        const count = Number(form.maxUsage); // maxUsage alanını kupon adedi olarak kullan
        const res = await Services.EPPetCreateMassCoupons(
          parsedUser?.token,
          title,
          mallID,
          discountForProduct,
          startDate,
          expireDate,
          discountType,
          discountValue,
          count
        );
        const resultCodes = Array.isArray(res?.data?.result)
          ? res.data.result.filter((x) => typeof x === "string" && x.trim() !== "")
          : [];

        if (isDuplicateCouponError(res?.data)) {
          toast.error(DUPLICATE_COUPON_MESSAGE);
          return;
        }

        if (isCouponCreateSuccess(res?.data)) {
          toast.success("Kupon başarılı bir şekilde oluşturulmuştur.");

          if (resultCodes.length > 0) {
            const locationTitle =
              locations.find((l) => String(l.id) === String(mallID))?.title || "-";

            setBulkMeta({
              title,
              locationTitle,
              startDate,
              expireDate,
            });
            setBulkCodes(resultCodes);
            setBulkSuccessModalOpen(true);
          }
        } else {
          const serverMsg = toTurkishMessage(
            res?.data?.message ||
            res?.data?.error ||
            "Kupon oluşturulamadı."
          );
          toast.error(serverMsg);
          return;
        }

        console.log("Mass Create:", res.data);
        onSubmit?.(res?.data ?? {
          title,
          mallID,
          discountForProduct,
          startDate,
          expireDate,
          discountType,
          discountValue,
          count,
          isBulk: true,
        });
      } else {
        // TEKİL KUPON OLUŞTURMA
        const inventory = form.maxUsage === "" ? null : Number(form.maxUsage);
        const couponCode = form.couponCode.trim();

        const res = await Services.EPPetCreateSingleCoupon(
          parsedUser?.token,
          title,
          mallID,
          discountForProduct,
          startDate,
          expireDate,
          discountType,
          discountValue,
          inventory,
          couponCode
        );
        if (isDuplicateCouponError(res?.data)) {
          toast.error(DUPLICATE_COUPON_MESSAGE);
          return;
        }

        if (isCouponCreateSuccess(res?.data)) {
          toast.success("Kupon başarılı bir şekilde oluşturulmuştur.");
        } else {
          const serverMsg = toTurkishMessage(
            res?.data?.message ||
            res?.data?.error ||
            "Kupon oluşturulamadı."
          );
          toast.error(serverMsg);
          return;
        }
        console.log("Single Create:", res.data);

        onSubmit?.(
          res?.data ?? {
            title,
            mallID,
            discountForProduct,
            startDate,
            expireDate,
            discountType,
            discountValue,
            inventory,
            couponCode,
            isBulk: false,
          }
        );
      }

      // handleClose();
    } catch (err) {
      console.error("Kupon oluşturulurken hata:", err);
      const serverMsg = toTurkishMessage(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Beklenmeyen bir hata oluştu."
      );
      setApiError(serverMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose?.();
    setForm(defaultForm);
    setErrors({});
    setApiError("");
    setIsSubmitting(false);
  };

  const exportBulkCodesToExcel = async () => {
    if (!bulkMeta || bulkCodes.length === 0) return;

    const rows = [
      {
        "Kampanya Adı": bulkMeta.title,
        "Kullanılacak AVM": bulkMeta.locationTitle,
        "Başlangıç Tarihi": bulkMeta.startDate,
        "Bitiş Tarihi": bulkMeta.expireDate,
      },
      {},
      { "Kampanya Adı": "Kupon Kodları" },
      ...bulkCodes.map((code) => ({ "Kampanya Adı": code })),
    ];

    try {
      const response = await api.post("excel/export", {
        type: "excel",
        content: rows,
        header: {
          "Kampanya Adı": "string",
          "Kullanılacak AVM": "string",
          "Başlangıç Tarihi": "string",
          "Bitiş Tarihi": "string",
        },
        sheetTitle: "Kupon Kodları",
      });

      if (response?.data?.status && response?.data?.result) {
        const w = window.open(response.data.result, "_blank");
        if (!w || w.closed || typeof w.closed === "undefined") {
          toast.error("Pop-up engellendi. Lütfen tarayıcı ayarlarını kontrol edin.");
        }
      } else {
        toast.error("Excel oluşturulamadı.");
      }
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Excel'e aktarım sırasında bir hata oluştu.");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 700,
          textAlign: "center",
          position: "relative",
        }}
      >
        KUPON OLUŞTUR
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}
          aria-label="Kapat"
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 2,
          pb: 3,
          px: 6,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Stack
          spacing={2}
          sx={{
            width: "100%",
            maxWidth: 700,
            mx: "auto",
          }}
        >
          {apiError ? (
            <Alert severity="error" variant="outlined">
              {apiError}
            </Alert>
          ) : null}

          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontWeight: 600 }}
          >
            Kupon Bilgileri
          </Typography>

          <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="flex-start"
          >
            <Grid item xs={12}>
              <TextField
                label="Kupon Adı"
                fullWidth
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.locationId}>
                <InputLabel id="location-label">Lokasyon</InputLabel>
                <Select
                  labelId="location-label"
                  label="Lokasyon"
                  value={form.locationId}
                  onChange={(e) => setField("locationId", e.target.value)}
                  IconComponent={KeyboardArrowDownRoundedIcon}
                >
                  {locations.map((l) => (
                    <MenuItem key={l.id} value={l.id}>
                      {l.title}
                    </MenuItem>
                  ))}
                </Select>
                {!!errors.locationId && (
                  <FormHelperText>{errors.locationId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="discount-for-product-label">Hizmet</InputLabel>
                <Select
                  labelId="discount-for-product-label"
                  label="Hizmet"
                  value={form.discountForProduct}
                  onChange={(e) => setField("discountForProduct", e.target.value)}
                  IconComponent={KeyboardArrowDownRoundedIcon}
                >
                  <MenuItem value="kid-car">Çocuk Arabası</MenuItem>
                  <MenuItem value="pet-car">Evcil Hayvan Arabası</MenuItem>
                  <MenuItem value="gift">Hediye Paketi</MenuItem>
                  <MenuItem value="all">Hepsi</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={trLocale}
            >
              <Grid item xs={12} md={6} display="flex" justifyContent="center">
                <DatePicker
                  label="Başlangıç Tarihi"
                  value={form.startDate}
                  onChange={(newValue) => setField("startDate", newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.startDate,
                      helperText: errors.startDate,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6} display="flex" justifyContent="center">
                <DatePicker
                  label="Bitiş Tarihi"
                  value={form.endDate}
                  onChange={(newValue) => setField("endDate", newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.endDate,
                      helperText: errors.endDate,
                    },
                  }}
                />
              </Grid>
            </LocalizationProvider>

            <Grid item xs={12} md={6} display="flex" justifyContent="center">
              <FormControl fullWidth error={!!errors.discountType}>
                <InputLabel id="discount-type-label">İndirim Tipi</InputLabel>
                <Select
                  labelId="discount-type-label"
                  label="İndirim Tipi"
                  value={form.discountType}
                  onChange={(e) => setField("discountType", e.target.value)}
                  IconComponent={KeyboardArrowDownRoundedIcon}
                >
                  <MenuItem value="percentage">Oran (%)</MenuItem>
                  <MenuItem value="amount">Miktar</MenuItem>
                </Select>
                {!!errors.discountType && (
                  <FormHelperText>{errors.discountType}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} display="flex" justifyContent="center">
              <TextField
                label={`İndirim Değeri (${discountTypeLabel})`}
                placeholder="Oran/Miktar"
                fullWidth
                value={form.discountValue}
                onChange={(e) => setField("discountValue", e.target.value)}
                error={!!errors.discountValue}
                helperText={errors.discountValue}
                inputProps={{ inputMode: "decimal" }}
              />
            </Grid>

            <Grid item xs={12} md={6} display="flex" justifyContent="center">
              <TextField
                label={maxUsageLabel}
                placeholder={form.isBulk ? "Oluşturulacak kupon sayısı" : "Maksimum Kullanım"}
                fullWidth
                value={form.maxUsage}
                onChange={(e) => setField("maxUsage", e.target.value)}
                error={!!errors.maxUsage}
                helperText={errors.maxUsage}
                inputProps={{ inputMode: "numeric" }}
              />
            </Grid>

            <Grid item xs={12} md={6} display="flex" justifyContent="center">
              <TextField
                label="Kupon Kodu"
                placeholder="Kupon kodu"
                fullWidth
                value={form.couponCode}
                onChange={(e) => setField("couponCode", e.target.value)}
                error={!!errors.couponCode}
                helperText={errors.couponCode}
                disabled={form.isBulk} // Toplu ise disabled
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.isBulk}
                    onChange={(e) => setField("isBulk", e.target.checked)}
                  />
                }
                label={
                  <span>
                    Toplu kupon oluşturmak istiyorum{" "}
                    <span style={{ color: "#ef4444" }}>
                      *(Her kupon kodu farklı oluşacaktır)
                    </span>
                  </span>
                }
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 6,
          pb: 3,
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{ borderRadius: "5px", textTransform: "none", px: 3 }}
        >
          Vazgeç
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={isSubmitting}
          sx={{ borderRadius: "5px", textTransform: "none", px: 3 }}
          startIcon={<KeyboardArrowDownRoundedIcon sx={{ transform: "rotate(-90deg)" }} />}
        >
          {isSubmitting ? "Oluşturuluyor..." : form.isBulk ? "Toplu Oluştur" : "Oluştur"}
        </Button>
      </DialogActions>

      <Dialog
        open={bulkSuccessModalOpen}
        onClose={() => setBulkSuccessModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Bilgilendirme</DialogTitle>
        <DialogContent>
          <Typography>
            Kodlar başarıyla oluştu, Excel'e aktarabilirsiniz.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkSuccessModalOpen(false)} variant="outlined">
            Kapat
          </Button>
          <Button onClick={exportBulkCodesToExcel} variant="contained">
            Excel'e Aktar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
